import React, { useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { USER_GUIDES, CATEGORY_COLORS } from '../../data/userGuides';
import { useGovernance } from '../../context/GovernanceContext';

const STATUS_CYCLE = { pending: 'deployed', deployed: 'completed', completed: 'pending' };

const STATUS_STYLES = {
  pending: 'bg-gray-100 text-gray-600',
  deployed: 'bg-blue-100 text-blue-700',
  completed: 'bg-emerald-100 text-emerald-700',
};

const STATUS_LABELS = { pending: 'Pending', deployed: 'Deployed', completed: 'Completed' };

function CategoryBadge({ category }) {
  const color = CATEGORY_COLORS[category] || '#6B7280';
  return (
    <span
      className="inline-block text-xs font-semibold px-2 py-0.5 rounded-full text-white"
      style={{ backgroundColor: color }}
    >
      {category}
    </span>
  );
}

function DeployedRow({ deployment, guide, onStatusChange, onNotesChange, onRemove }) {
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesValue, setNotesValue] = useState(deployment.notes || '');

  const handleNotesBlur = () => {
    setEditingNotes(false);
    if (notesValue !== deployment.notes) {
      onNotesChange(deployment.id, notesValue);
    }
  };

  return (
    <div className="flex items-start gap-3 py-3 border-b border-gray-100 last:border-0">
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2 mb-1">
          <span className="text-sm font-medium text-brand-navy truncate">{guide.title}</span>
          <CategoryBadge category={guide.category} />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => onStatusChange(deployment.id, STATUS_CYCLE[deployment.status])}
            className={`text-xs font-semibold px-2 py-0.5 rounded-full cursor-pointer transition-opacity hover:opacity-80 ${STATUS_STYLES[deployment.status]}`}
            title="Click to advance status"
          >
            {STATUS_LABELS[deployment.status]}
          </button>
          {editingNotes ? (
            <textarea
              autoFocus
              value={notesValue}
              onChange={(e) => setNotesValue(e.target.value)}
              onBlur={handleNotesBlur}
              rows={2}
              className="text-xs border border-gray-200 rounded px-2 py-1 resize-none focus:outline-none focus:ring-1 focus:ring-brand-teal w-48"
            />
          ) : (
            <span
              className="text-xs text-mid-grey cursor-pointer hover:text-brand-navy flex items-center gap-1"
              onClick={() => setEditingNotes(true)}
              title="Click to edit notes"
            >
              {notesValue || <span className="italic text-gray-300">Add notes…</span>}
              <svg className="w-3 h-3 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeWidth="2" strokeLinecap="round" d="M15.232 5.232l3.536 3.536M9 13l6.768-6.768a2 2 0 012.828 2.828L11.828 15.828a2 2 0 01-.828.572l-3 1 1-3a2 2 0 01.572-.828z" />
              </svg>
            </span>
          )}
        </div>
      </div>
      <button
        onClick={() => onRemove(deployment.id)}
        className="shrink-0 text-gray-300 hover:text-red-500 transition-colors p-1"
        title="Remove from plan"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeWidth="2" strokeLinecap="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}

export default function ClientGuides() {
  const { id } = useParams();
  const {
    clients,
    loading,
    getClientDeployments,
    deployGuide,
    updateGuideDeployment,
    removeGuideDeployment,
  } = useGovernance();

  const [statusFilter, setStatusFilter] = useState('All');
  const [adding, setAdding] = useState(null); // slug being added

  const client = clients.find((c) => c.id === id);
  const deployments = getClientDeployments(id);
  const deployedSlugs = new Set(deployments.map((d) => d.guide_slug));

  const filteredDeployments = deployments.filter(
    (d) => statusFilter === 'All' || d.status === statusFilter.toLowerCase()
  );

  const guidesInLibrary = USER_GUIDES.filter((g) => !deployedSlugs.has(g.slug));

  const handleStatusChange = useCallback(
    async (depId, newStatus) => {
      const fields = { status: newStatus };
      if (newStatus === 'deployed') fields.deployed_at = new Date().toISOString();
      await updateGuideDeployment(depId, fields);
    },
    [updateGuideDeployment]
  );

  const handleNotesChange = useCallback(
    async (depId, notes) => {
      await updateGuideDeployment(depId, { notes });
    },
    [updateGuideDeployment]
  );

  const handleAddToplan = useCallback(
    async (slug) => {
      setAdding(slug);
      try {
        await deployGuide(id, slug, '');
      } finally {
        setAdding(null);
      }
    },
    [deployGuide, id]
  );

  if (loading) return <div className="text-mid-grey text-sm">Loading…</div>;
  if (!client) {
    return (
      <div className="text-center py-16">
        <p className="text-mid-grey">Client not found.</p>
        <Link to="/clients" className="text-brand-teal text-sm hover:underline mt-2 block">
          ← Back to clients
        </Link>
      </div>
    );
  }

  return (
    <div>
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-sm text-mid-grey mb-5">
        <Link to="/clients" className="hover:text-brand-navy">Clients</Link>
        <span>/</span>
        <Link to={`/clients/${id}`} className="hover:text-brand-navy">{client.name}</Link>
        <span>/</span>
        <span className="text-brand-navy font-medium">Guides</span>
      </nav>

      <div className="mb-6">
        <h1 className="text-2xl font-bold text-brand-navy">{client.name} — Guide Deployments</h1>
        <p className="text-sm text-mid-grey mt-1">
          Track which training guides have been assigned and their deployment status.
        </p>
      </div>

      {/* Deployed plan */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6">
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <h2 className="text-sm font-semibold text-brand-navy">Deployed plan</h2>
          {/* Status filter pills */}
          <div className="flex flex-wrap gap-1.5">
            {['All', 'Pending', 'Deployed', 'Completed'].map((s) => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={`text-xs font-medium px-2.5 py-1 rounded-full border transition-colors ${
                  statusFilter === s
                    ? 'bg-brand-navy text-white border-brand-navy'
                    : 'bg-white text-mid-grey border-gray-200 hover:border-brand-navy hover:text-brand-navy'
                }`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        {filteredDeployments.length > 0 ? (
          <div>
            {filteredDeployments.map((dep) => {
              const guide = USER_GUIDES.find((g) => g.slug === dep.guide_slug);
              if (!guide) return null;
              return (
                <DeployedRow
                  key={dep.id}
                  deployment={dep}
                  guide={guide}
                  onStatusChange={handleStatusChange}
                  onNotesChange={handleNotesChange}
                  onRemove={removeGuideDeployment}
                />
              );
            })}
          </div>
        ) : (
          <div className="text-center py-8">
            <p className="text-sm text-mid-grey">
              {deployments.length === 0
                ? 'No guides in this client\'s plan yet. Add some from the library below.'
                : 'No guides match the selected filter.'}
            </p>
          </div>
        )}
      </div>

      {/* Library — add more guides */}
      {guidesInLibrary.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h2 className="text-sm font-semibold text-brand-navy mb-4">
            Library — add more guides
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {guidesInLibrary.map((guide) => (
              <div
                key={guide.slug}
                className="border border-gray-100 rounded-lg p-3 flex flex-col gap-2"
              >
                <div className="flex items-start justify-between gap-2">
                  <CategoryBadge category={guide.category} />
                  <span className="text-xs text-mid-grey whitespace-nowrap">
                    {guide.readingTime} min
                  </span>
                </div>
                <p className="text-xs font-medium text-brand-navy leading-snug">{guide.title}</p>
                <p className="text-xs text-mid-grey leading-relaxed line-clamp-2 flex-1">
                  {guide.summary}
                </p>
                <button
                  onClick={() => handleAddToplan(guide.slug)}
                  disabled={adding === guide.slug}
                  className="text-xs font-semibold text-brand-teal border border-brand-teal rounded-lg px-2 py-1 hover:bg-brand-teal hover:text-white transition-colors disabled:opacity-50 self-start"
                >
                  {adding === guide.slug ? 'Adding…' : '+ Add to plan'}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
